/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFloatingDropdownMenu } from '../../hooks/useFloatingDropdownMenu';
import { ComponentIcon as Icon } from './ComponentIcon';
import type { UserGroupDirectoryKind } from '../../hooks/useUserGroupDirectory';
import './UserGroupSelect.css';

export interface UserGroupSelectOption {
  value: string;
  label: string;
  kind: UserGroupDirectoryKind;
  searchStr?: string;
  disabled?: boolean;
}

interface UserGroupSelectProps {
  id?: string;
  options: UserGroupSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  noResultsText: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  variant?: 'form' | 'compact';
  ariaLabel?: string;
}

const KIND_RANK: Record<UserGroupDirectoryKind, number> = {
  systemGroup: 1,
  group: 2,
  user: 3,
};

const normalizeSearchText = (value: string): string =>
  value.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');

function iconName(kind: UserGroupDirectoryKind): string {
  return kind === 'user' ? 'user' : 'users';
}

export const UserGroupSelect: React.FC<UserGroupSelectProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  noResultsText,
  loading = false,
  loadingText = 'Yükleniyor...',
  disabled = false,
  variant = 'form',
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { dropdownRef, dropdownStyle } = useFloatingDropdownMenu<HTMLDivElement>({
    open,
    anchorRef,
    minWidth: variant === 'compact' ? 240 : 280,
    minHeight: 120,
    maxHeight: 320,
  });

  const sortedOptions = useMemo(() => [...options].sort((left, right) => {
    const rank = KIND_RANK[left.kind] - KIND_RANK[right.kind];
    return rank || left.label.localeCompare(right.label);
  }), [options]);

  const filteredOptions = useMemo(() => {
    const needle = normalizeSearchText(search.trim());
    if (!needle) return sortedOptions;
    return sortedOptions.filter((option) =>
      normalizeSearchText(option.searchStr || option.label).includes(needle));
  }, [search, sortedOptions]);

  const selected = options.find((option) => option.value === value);
  const interactive = !disabled && !loading;

  useEffect(() => {
    if (!open) setSearch('');
    if (open) window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [dropdownRef, open]);

  const dropdown = open && interactive ? (
    <div
      ref={dropdownRef}
      className="user-group-select__menu"
      style={dropdownStyle}
      role="listbox"
      aria-label={ariaLabel || placeholder}
    >
      <div className="user-group-select__search">
        <Icon name="search" size={16} />
        <input
          ref={inputRef}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div className="user-group-select__options">
        {filteredOptions.length === 0 ? (
          <div className="user-group-select__empty" role="status">{noResultsText}</div>
        ) : filteredOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`user-group-select__option${option.value === value ? ' is-selected' : ''}`}
            role="option"
            aria-selected={option.value === value}
            disabled={option.disabled}
            onClick={() => {
              if (option.value !== value) onChange(option.value);
              setOpen(false);
            }}
          >
            <Icon name={iconName(option.kind)} size={15} />
            <span>{option.label}</span>
            {option.value === value ? <Icon name="check" size={15} /> : null}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={anchorRef}
        id={id}
        type="button"
        className={`user-group-select user-group-select--${variant}${open ? ' is-open' : ''}${!selected ? ' is-empty' : ''}`}
        onClick={() => interactive && setOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || placeholder}
        title={selected?.label || placeholder}
      >
        <span className="user-group-select__current">
          {selected ? <Icon name={iconName(selected.kind)} size={15} /> : <Icon name="user" size={15} />}
          <span>{loading ? loadingText : (selected?.label || placeholder)}</span>
        </span>
        <Icon name="expandMore" size={16} className="user-group-select__chevron" />
      </button>
      {typeof document !== 'undefined' && dropdown ? createPortal(dropdown, document.body) : null}
    </>
  );
};
